import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Theater, ImagePlus, Trash2, Save, Sparkles, LogIn } from "lucide-react";
import { fmtDate } from "@/lib/format";
import OnboardWizard from "@/pages/platform/OnboardWizard";
import { Switch } from "@/components/ui/switch";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

export default function PlatformProductions() {
  const { selectProduction } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [theaterName, setTheaterName] = useState("");
  const [auditionPublic, setAuditionPublic] = useState(false);
  const [auditionPitch, setAuditionPitch] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/platform/productions").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter((p) =>
      (p.title || "").toLowerCase().includes(term) ||
      (p.theater_name || "").toLowerCase().includes(term) ||
      (p.director || "").toLowerCase().includes(term) ||
      (p.venue || "").toLowerCase().includes(term)
    );
  }, [items, q]);

  const openEditor = (p) => {
    setSelected(p);
    setTheaterName(p.theater_name || "");
    setAuditionPublic(!!p.audition_public);
    setAuditionPitch(p.audition_pitch || "");
  };
  const closeEditor = () => { setSelected(null); setTheaterName(""); setAuditionPublic(false); setAuditionPitch(""); };

  const saveName = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.patch(`/productions/${selected.id}`, { theater_name: theaterName, audition_public: auditionPublic, audition_pitch: auditionPitch });
      toast.success("Branding saved");
      await load();
      const fresh = (await api.get("/platform/productions")).data.find((p) => p.id === selected.id);
      if (fresh) setSelected(fresh);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file || !selected) return;
    const form = new FormData(); form.append("file", file);
    const token = localStorage.getItem("sc_token");
    try {
      const res = await fetch(`${API_BASE}/productions/${selected.id}/theater-logo`, {
        method: "POST", body: form,
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Upload failed");
      toast.success("Logo uploaded");
      await load();
      const fresh = (await api.get("/platform/productions")).data.find((p) => p.id === selected.id);
      if (fresh) setSelected(fresh);
    } catch (e) { toast.error(e.message || "Upload failed"); }
  };

  const removeLogo = async () => {
    if (!selected) return;
    try {
      await api.delete(`/productions/${selected.id}/theater-logo`);
      toast.success("Logo removed");
      await load();
      const fresh = (await api.get("/platform/productions")).data.find((p) => p.id === selected.id);
      if (fresh) setSelected(fresh);
    } catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
  };

  const enterAs = (p) => {
    selectProduction(p.id);
    sessionStorage.setItem("sc_impersonating", "1");
    nav("/dashboard");
  };

  return (
    <div className="space-y-6" data-testid="platform-productions">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-gold mb-1">Platform Admin</div>
          <h1 className="font-display text-4xl sm:text-5xl">All Productions</h1>
          <p className="text-zinc-500 text-sm mt-2">Every production across the platform. Click a row to edit theatre branding.</p>
        </div>
        <button data-testid="onboard-client-btn" onClick={() => setWizardOpen(true)} style={{ backgroundColor: "#F59E0B", color: "#0A0A0A" }} className="h-10 px-4 rounded font-semibold text-sm inline-flex items-center gap-2 hover:brightness-110">
          <Sparkles className="w-4 h-4" /> Onboard client
        </button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input data-testid="platform-productions-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, theatre, director, venue" className="pl-9 bg-[#0F0F0F] border-white/10 h-10" />
        </div>
        <div className="text-xs text-zinc-500">{filtered.length} of {items.length}</div>
      </div>

      <div className="rounded-lg border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#121212] text-zinc-500 text-[10px] uppercase tracking-[0.2em]">
            <tr>
              <th className="text-left p-3 w-14"></th>
              <th className="text-left p-3">Production</th>
              <th className="text-left p-3">Theatre</th>
              <th className="text-left p-3">Members</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Created</th>
              <th className="text-left p-3">Enroll code</th>
              <th className="text-right p-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} onClick={() => openEditor(p)} data-testid={`platform-prod-row-${p.id}`}
                  className="border-t border-white/5 hover:bg-white/[0.02] cursor-pointer">
                <td className="p-3">
                  {p.theater_logo_url ? (
                    <img src={p.theater_logo_url} alt="" className="w-10 h-10 rounded object-contain bg-[#0A0A0A] border border-white/10 p-0.5" />
                  ) : (
                    <div className="w-10 h-10 rounded bg-[#0A0A0A] border border-white/10 grid place-items-center text-zinc-600">
                      <Theater className="w-4 h-4" />
                    </div>
                  )}
                </td>
                <td className="p-3">
                  <div className="font-medium">{p.title}</div>
                  {p.director && <div className="text-[11px] text-zinc-500">Dir. {p.director}</div>}
                </td>
                <td className="p-3 text-zinc-300">{p.theater_name || <span className="text-zinc-600">—</span>}</td>
                <td className="p-3 text-zinc-300">{p.member_count}</td>
                <td className="p-3">
                  <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded border ${p.status === "archived" ? "border-zinc-600 text-zinc-500" : "border-gold/40 text-gold"}`}>{p.status || "active"}</span>
                </td>
                <td className="p-3 text-zinc-500 text-xs">{p.created_at ? fmtDate(p.created_at) : "—"}</td>
                <td className="p-3 text-zinc-400 font-mono text-xs">{p.enroll_code}</td>
                <td className="p-3 text-right">
                  <button
                    data-testid={`impersonate-${p.id}`}
                    onClick={(e) => { e.stopPropagation(); enterAs(p); }}
                    className="h-8 px-2.5 rounded border border-gold/40 text-gold hover:bg-gold/10 text-xs inline-flex items-center gap-1.5 whitespace-nowrap"
                    title="Enter this production as super admin"
                  >
                    <LogIn className="w-3.5 h-3.5" /> Enter
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-zinc-500">No productions match your search.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && closeEditor()}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">Edit branding — {selected?.title}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4 pt-2">
              <div className="rounded-lg border border-white/10 p-4 bg-[#0F0F0F] flex items-center gap-4">
                {selected.theater_logo_url ? (
                  <img src={selected.theater_logo_url} alt="" className="w-20 h-20 rounded object-contain bg-[#0A0A0A] border border-white/10 p-1" />
                ) : (
                  <div className="w-20 h-20 rounded bg-[#0A0A0A] border border-white/10 grid place-items-center text-zinc-600">
                    <Theater className="w-6 h-6" />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <input ref={fileRef} type="file" accept="image/*" onChange={(e) => uploadLogo(e.target.files?.[0])} className="hidden" data-testid="platform-logo-input" />
                  <button data-testid="platform-upload-logo-btn" onClick={() => fileRef.current?.click()} className="w-full h-9 rounded border border-white/15 text-sm text-zinc-200 hover:bg-white/5 inline-flex items-center justify-center gap-2">
                    <ImagePlus className="w-4 h-4" /> {selected.theater_logo_url ? "Replace logo" : "Upload logo"}
                  </button>
                  {selected.theater_logo_url && (
                    <button data-testid="platform-remove-logo-btn" onClick={removeLogo} className="w-full h-9 rounded border border-red-500/30 text-red-400 text-sm hover:bg-red-500/10 inline-flex items-center justify-center gap-2">
                      <Trash2 className="w-4 h-4" /> Remove logo
                    </button>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-zinc-300 text-xs uppercase tracking-widest">Theatre name</Label>
                <Input data-testid="platform-theater-name-input" value={theaterName} onChange={(e) => setTheaterName(e.target.value)} placeholder="e.g. The Willoughby Theatre" className="mt-2 bg-[#121212] border-white/10 h-11" />
              </div>

              <div className="flex items-start justify-between rounded-lg border border-white/10 bg-[#121212] p-3">
                <div className="pr-2">
                  <div className="text-sm text-zinc-100">Publish audition to public directory</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">Show on the public <a href="/discover" target="_blank" rel="noreferrer" className="text-gold underline">/discover</a> page.</div>
                </div>
                <Switch data-testid="platform-publish-audition" checked={auditionPublic} onCheckedChange={setAuditionPublic} />
              </div>

              <div>
                <Label className="text-zinc-300 text-xs uppercase tracking-widest">Public audition pitch</Label>
                <textarea data-testid="platform-audition-pitch" rows={2} value={auditionPitch} onChange={(e) => setAuditionPitch(e.target.value)} placeholder="One-line hook for auditioners on the public directory (falls back to tagline)." className="mt-2 w-full bg-[#121212] border border-white/10 rounded p-2 text-sm text-zinc-200" />
              </div>

              <div className="text-xs text-zinc-500 space-y-1">
                <div>Enroll code: <span className="font-mono text-zinc-300">{selected.enroll_code}</span></div>
                <div>Members: <span className="text-zinc-300">{selected.member_count}</span></div>
                <div>Admins: <span className="text-zinc-300">{(selected.admins || []).map((a) => `${a.name} <${a.email}>`).join(", ") || "—"}</span></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={closeEditor} className="h-9 px-3 rounded border border-white/10 text-sm text-zinc-300 hover:bg-white/5">Close</button>
            <button data-testid="platform-save-branding-btn" onClick={saveName} disabled={saving} style={goldBtn} className="h-9 px-4 rounded font-semibold text-sm inline-flex items-center gap-1.5">
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save name"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OnboardWizard open={wizardOpen} onClose={() => setWizardOpen(false)} onDone={load} />
    </div>
  );
}
